import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { LoadingBlock, ErrorState } from "@/components/ds";
import { PageTitle, Badge, Card, money } from "./_ui";

export default function SalesLedger() {
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [selected, setSelected] = useState<string | null>(null);
  const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
  const { data, isLoading, isError, refetch } = useQuery({ queryKey: ["sales-ledger", params], queryFn: () => api.salesLedger(params) });

  const set = (k: string, v: string) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <div data-testid="sales-ledger">
      <PageTitle title="Sales Ledger" subtitle="Every qualifying paid sale. Each row is one commission record." />
      <div className="mb-4 flex flex-wrap gap-2 text-sm" data-testid="ledger-filters">
        <select onChange={(e) => set("plan", e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="filter-plan">
          <option value="">All plans</option>{["starter","growth","pro","custom"].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select onChange={(e) => set("status", e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="filter-status">
          <option value="">All statuses</option>{["earned","held","reversed","adjusted","paid","pending"].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select onChange={(e) => set("payout_status", e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="filter-payout">
          <option value="">All payout</option>{["unpaid","paid"].map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
        <select onChange={(e) => set("promotion", e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="filter-promo">
          <option value="">All promotions</option><option value="growth_intro_25">growth_intro_25</option>
        </select>
        <input type="date" onChange={(e) => set("date_from", e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="filter-from" />
        <input type="date" onChange={(e) => set("date_to", e.target.value)} className="rounded-md border border-slate-200 px-2 py-1" data-testid="filter-to" />
      </div>

      {isLoading ? <LoadingBlock rows={6} /> : isError || !data ? <ErrorState onRetry={refetch} /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-slate-100 text-left text-xs text-slate-400">
              {["Date","Business","Plan","Std Initial","Promotion","Collected","Basis","%","Commission","Stripe/Refund","Payout"].map((c) => <th key={c} className="px-3 py-2 whitespace-nowrap">{c}</th>)}
            </tr></thead>
            <tbody>
              {data.sales.length === 0 && <tr><td colSpan={11} className="px-3 py-8 text-center text-slate-400">No sales match these filters.</td></tr>}
              {data.sales.map((s: any) => (
                <tr key={s.id} className="cursor-pointer border-b border-slate-50 hover:bg-slate-50" onClick={() => setSelected(s.id)} data-testid={`ledger-row-${s.id}`}>
                  <td className="px-3 py-2">{String(s.sale_date).slice(0,10)}</td>
                  <td className="px-3 py-2">{s.business_name}</td>
                  <td className="px-3 py-2 capitalize">{s.plan}</td>
                  <td className="px-3 py-2">{money(s.standard_monthly_price + s.standard_setup_fee)}</td>
                  <td className="px-3 py-2">{s.promotion_used || "—"}</td>
                  <td className="px-3 py-2">{money(s.total_actual_collected)}</td>
                  <td className="px-3 py-2 text-xs">{s.commission_basis}</td>
                  <td className="px-3 py-2">{s.commission_percent}%</td>
                  <td className="px-3 py-2 font-medium">{money(s.commission_amount)}</td>
                  <td className="px-3 py-2"><Badge status={s.refund_status !== "none" ? s.refund_status : s.commission_status} /></td>
                  <td className="px-3 py-2"><Badge status={s.payout_status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selected && <SaleDetail saleId={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function SaleDetail({ saleId, onClose }: { saleId: string; onClose: () => void }) {
  const { data, isLoading } = useQuery({ queryKey: ["sale-detail", saleId], queryFn: () => api.salesSaleDetail(saleId) });
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4 pt-16" onClick={onClose} data-testid="sale-detail-modal">
      <div className="max-h-[80vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-6 shadow-xl" onClick={(e) => e.stopPropagation()}>
        {isLoading || !data ? <LoadingBlock rows={5} /> : (
          <>
            <div className="mb-4 flex items-center justify-between">
              <h2 className="text-lg font-semibold">{data.business_name} — {data.plan}</h2>
              <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="sale-detail-close">✕</button>
            </div>
            <div className="grid gap-4 sm:grid-cols-2 text-sm">
              <Card title="Pricing"><dl className="space-y-1">
                <L k="Standard monthly" v={money(data.standard_monthly_price)} />
                <L k="Standard setup" v={money(data.standard_setup_fee)} />
                <L k="Actual first month" v={money(data.actual_first_month_collected)} />
                <L k="Actual setup" v={money(data.actual_setup_collected)} />
                <L k="Total collected" v={money(data.total_actual_collected)} />
                <L k="Company-funded discount" v={money(data.company_funded_discount)} />
              </dl></Card>
              <Card title="Compensation"><dl className="space-y-1">
                <L k="Commissionable" v={money(data.commissionable_value)} />
                <L k="Commission basis" v={data.commission_basis} />
                <L k="Rate" v={`${data.commission_percent}%`} />
                <L k="Commission" v={money(data.commission_amount)} />
                <L k="Plan version" v={data.commission_plan_version} />
                <L k="Period" v={`${String(data.period_start).slice(0,10)} → ${String(data.period_end).slice(0,10)}`} />
              </dl></Card>
              <Card title="Payment Status"><dl className="space-y-1">
                <L k="Commission" v={<Badge status={data.commission_status} />} />
                <L k="Refund" v={<Badge status={data.refund_status} />} />
                <L k="Chargeback" v={<Badge status={data.chargeback_status} />} />
                <L k="Payout" v={<Badge status={data.payout_status} />} />
              </dl></Card>
              <Card title="Sale"><dl className="space-y-1">
                <L k="Rep" v={data.sales_rep_user_id} />
                <L k="Organization" v={data.organization_id} />
                <L k="Sale date" v={String(data.sale_date).slice(0,19)} />
                <L k="Checkout session" v={data.checkout_session_id || "—"} />
              </dl></Card>
            </div>
            <Card title="Audit History"><ul className="space-y-1 text-xs text-slate-600" data-testid="sale-audit">
              {(data.audit || []).map((a: any, i: number) => (
                <li key={i}>{String(a.at).slice(0,19)} · <b>{a.action}</b> · {a.detail}{a.reason ? ` · ${a.reason}` : ""} <span className="text-slate-400">by {a.by}</span></li>
              ))}
            </ul></Card>
          </>
        )}
      </div>
    </div>
  );
}

function L({ k, v }: { k: string; v: React.ReactNode }) {
  return <div className="flex justify-between gap-2"><dt className="text-slate-500">{k}</dt><dd className="font-medium text-slate-900 text-right break-all">{v}</dd></div>;
}
